import { useEffect, useRef, useState } from "react";
import type { ChatAttachment } from "../../../types";
import { mediaUrl } from "../../../mediaAuth";
import { getAutolockBridge } from "../../../autolockBridge";
import { formatDuration, isIOS, canPlayOnIOS } from "../../../utils";
import { WaveformBars } from "./WaveformBars";

const SPEEDS = [1, 1.5, 2] as const;

function safePlay(audio: HTMLAudioElement): void {
  audio.play()?.catch(() => {});
}

export function VoiceMessagePlayer({ attachment }: { attachment: ChatAttachment }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(attachment.durationSec ?? 0);
  const [speedIndex, setSpeedIndex] = useState(0);

  // Same iOS-can't-play-webm gap that video attachments already work around
  // (MessageBubble.tsx's Attachment component) - webm is the default
  // MediaRecorder output on Chrome/Firefox, but iOS Safari can't decode it.
  const unplayableOnIOS = isIOS() && !canPlayOnIOS(attachment.filename);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onPlay = () => {
      setIsPlaying(true);
      getAutolockBridge()?.pauseInactivityForMedia();
    };
    const onPauseOrEnd = () => {
      setIsPlaying(false);
      getAutolockBridge()?.resumeInactivityAfterMedia();
    };
    const onEnded = () => {
      onPauseOrEnd();
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPauseOrEnd);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPauseOrEnd);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  if (unplayableOnIOS) {
    return (
      <div className="chat-voice-player">
        <a href={mediaUrl(`/api/chat/media/${attachment.filename}`)} target="_blank" rel="noopener">
          Open voice message
        </a>
      </div>
    );
  }

  const src = attachment.localBlobUrl ?? mediaUrl(`/api/chat/media/${attachment.filename}`);

  function togglePlay(): void {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) safePlay(audio);
    else audio.pause();
  }

  function cycleSpeed(): void {
    const nextIndex = (speedIndex + 1) % SPEEDS.length;
    setSpeedIndex(nextIndex);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[nextIndex];
  }

  function handleSeek(fraction: number): void {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = fraction * duration;
    setCurrentTime(audio.currentTime);
  }

  const displayTime = isPlaying || currentTime > 0 ? currentTime : duration;

  return (
    <div className="chat-voice-player">
      <audio ref={audioRef} src={src} preload="metadata" hidden />
      <button
        type="button"
        className="chat-voice-play-btn"
        aria-label={isPlaying ? "Pause voice message" : "Play voice message"}
        onClick={togglePlay}
      >
        <i className={`bi ${isPlaying ? "bi-pause-fill" : "bi-play-fill"}`} aria-hidden="true" />
      </button>
      <WaveformBars
        levels={attachment.waveform ?? []}
        progress={duration ? currentTime / duration : 0}
        onSeek={handleSeek}
        className="chat-waveform-playback"
      />
      <span className="chat-voice-duration">{formatDuration(displayTime)}</span>
      <button type="button" className="chat-voice-speed-btn" aria-label="Change playback speed" onClick={cycleSpeed}>
        {SPEEDS[speedIndex]}x
      </button>
    </div>
  );
}
